import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';

export const METASPACES_CACHE_VERSION = 2;

/**
 * Resolution state for a single metaspace ref (system_name or id) that was NOT in
 * the list fetched at app init. Tracked per ref so a route can render what actually
 * happened — still loading, absent, or refused — instead of a blank page.
 */
export type MetaspaceRefStatus =
  | 'loading'
  | 'found'
  | 'not_found'
  | 'forbidden'
  | 'error';

interface MetaspacesState {
  bySphere: Record<string, any[]>;
  /** ref (system_name or id) -> outcome of the on-demand lookup for it. */
  byRef: Record<string, MetaspaceRefStatus>;
  metaspacesCacheVersion: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: MetaspacesState = {
  bySphere: {},
  byRef: {},
  metaspacesCacheVersion: METASPACES_CACHE_VERSION,
  status: 'idle',
  error: null,
};

export const fetchMetaspaces = createAsyncThunk<
  { metaspaces: any[] },
  { apiClient: ApiClient },
  { rejectValue: string }
>(
  'metaspaces/fetchMetaspaces',
  async ({ apiClient }, thunkAPI) => {
    try {
      const data = await apiClient.get<{ metaspaces: any[] }>('/metaspaces');
      return data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error');
    }
  }
);

/**
 * Resolve a single metaspace on demand, for a route pointing at one the init-time
 * list did not include (a deep link, a bookmark, or one deployed mid-session).
 *
 * Goes through the LIST route's `system_names` filter rather than
 * `GET /metaspaces/:id` on purpose. `listMetaspaces` gates non-`platform_global`
 * rows behind a SpiceDB `view` check; `getMetaSpaceById` performs no per-resource
 * check at all, so resolving through it would let a deep link surface a metaspace
 * the list route would have withheld. The two read paths disagree about who may
 * see a metaspace (tracked separately) — this takes the stricter one.
 *
 * A consequence worth knowing: a ref that is a bare `metaspace_id` cannot be
 * resolved this way, because the filter matches on `system_name`. That is
 * deliberate. Such a ref only misses when the list already withheld the row, and
 * widening access to recover it is the wrong trade.
 */
export const fetchMetaspaceByRef = createAsyncThunk<
  { ref: string; metaspaces: any[] },
  { apiClient: ApiClient; ref: string },
  { rejectValue: { ref: string; status: MetaspaceRefStatus; message: string } }
>(
  'metaspaces/fetchMetaspaceByRef',
  async ({ apiClient, ref }, thunkAPI) => {
    try {
      const data = await apiClient.get<{ metaspaces: any[] }>(
        `/metaspaces?system_names=${encodeURIComponent(ref)}`
      );
      return { ref, metaspaces: data?.metaspaces ?? [] };
    } catch (err: any) {
      const code = err?.response?.status;
      // 404 is the list route's "nothing matched" — indistinguishable from a row
      // the permission filter dropped, which is why both read as "not available"
      // to the user rather than being reported as an error.
      const STATUS_BY_HTTP_CODE: Record<number, MetaspaceRefStatus> = {
        404: 'not_found',
        403: 'forbidden',
      };
      const status: MetaspaceRefStatus = STATUS_BY_HTTP_CODE[code] ?? 'error';
      return thunkAPI.rejectWithValue({
        ref,
        status,
        message: err?.message ?? 'Unknown error',
      });
    }
  }
);

const metaspacesSlice = createSlice({
  name: 'metaspaces',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetaspaces.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(
        fetchMetaspaces.fulfilled,
        (state, action: PayloadAction<{ metaspaces: any[] }>) => {
          state.status = 'succeeded';
          state.bySphere['$global'] = action.payload.metaspaces;
        }
      )
      .addCase(fetchMetaspaces.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(fetchMetaspaceByRef.pending, (state, action) => {
        state.byRef[action.meta.arg.ref] = 'loading';
      })
      .addCase(fetchMetaspaceByRef.fulfilled, (state, action) => {
        const { ref, metaspaces } = action.payload;
        if (metaspaces.length === 0) {
          // 200 with an empty list — same user-visible outcome as a 404.
          state.byRef[ref] = 'not_found';
          return;
        }
        // Merged into the same bucket the init fetch populates, so every consumer
        // (this route, the switcher, the nav bar) sees it without a second lookup.
        // Deduped by id: re-entering the route must not append a duplicate.
        const existing = state.bySphere['$global'] ?? [];
        const known = new Set(existing.map((m: any) => m.metaspace_id));
        state.bySphere['$global'] = [
          ...existing,
          ...metaspaces.filter((m: any) => !known.has(m.metaspace_id)),
        ];
        state.byRef[ref] = 'found';
      })
      .addCase(fetchMetaspaceByRef.rejected, (state, action) => {
        const ref = action.meta.arg.ref;
        state.byRef[ref] = action.payload?.status ?? 'error';
      });
  },
});

export default metaspacesSlice.reducer;

// Memoized so it returns a stable array reference while `bySphere` is unchanged.
// A raw `Object.values().flat()` produces a new array on every call, which makes
// `useSelector(selectAllMetaspaces)` re-render on every dispatch — and any consumer
// with a `[metaspaces]` effect dependency then re-fires forever (render → dispatch →
// new array → render), driving a full re-render/re-fetch loop across the metaspace tree.
export const selectAllMetaspaces = createSelector(
  (state: { metaspaces: MetaspacesState }) => state.metaspaces.bySphere,
  (bySphere) => Object.values(bySphere).flat()
);

/** Outcome of the on-demand lookup for `ref`, or undefined if none was attempted. */
export const selectMetaspaceRefStatus = (
  state: { metaspaces: MetaspacesState },
  ref: string
): MetaspaceRefStatus | undefined => state.metaspaces.byRef[ref];

/** Status/error of the init-time list fetch, so a failed load can be told from an empty one. */
export const selectMetaspacesListStatus = (state: {
  metaspaces: MetaspacesState;
}) => state.metaspaces.status;

export const selectMetaspacesListError = (state: {
  metaspaces: MetaspacesState;
}) => state.metaspaces.error;
